import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  CircularProgress, 
  Alert, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Card, 
  CardContent,
  Tabs,
  Tab,
  Chip,
  Avatar,
  IconButton,
  useTheme,
  FormControlLabel,
  Radio
} from '@mui/material';
import TooltipWrapper from '../common/TooltipWrapper';
import { styled } from '@mui/material/styles';
import {
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  ExitToApp as ExitToAppIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { dischargePatientFromBed } from '../../services/bedAssignmentService';
import { dischargePatientFromRoom } from '../../services/roomAssignmentService';
import { useAuth } from '../../context/AuthContext';
import { DatePicker } from '@mui/x-date-pickers';
import { InpatientService } from '../../services/inpatientService';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const CompactCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  marginBottom: theme.spacing(2),
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5),
  }
}));

const PatientDischarge = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  const [encounters, setEncounters] = useState([]);
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [dischargeFormData, setDischargeFormData] = useState({});
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Fetch active inpatient encounters (optimized)
  const fetchEncounters = useCallback(async () => {
    setLoading(true);
    try {
      // Get facility ID from user's default facility
      const facilityId = user?.defaultFacility?.id;
      
      console.log('PatientDischarge: User object:', user);
      console.log('PatientDischarge: Facility ID:', facilityId);
      
      if (!facilityId) {
        console.warn('No facility ID available for user:', user);
        setEncounters([]);
        setLoading(false);
        return;
      }

      console.log('PatientDischarge: Making API call to fetch inpatient encounters for facility:', facilityId);
      const response = await InpatientService.getInpatientEncounters(facilityId);
      
      console.log('PatientDischarge: API response:', response);
      
      const encountersData = response.encounters || [];
      console.log('PatientDischarge: Raw encounters data:', encountersData);
      
      // Process encounters and extract patient information
      const encountersWithPatients = encountersData.map((encounter) => {
        // Extract basic encounter data
        const encounterData = { 
          id: encounter.id, 
          patientId: encounter.patientId,
          type: encounter.type,
          status: encounter.status,
          startDate: encounter.startDate || encounter.admissionDate,
          diagnosis: encounter.diagnosis || encounter.chiefComplaint || '',
          reason: encounter.reason || encounter.chiefComplaint || '',
          bedUnit: encounter.bed || encounter.bedUnit || '',
          locationId: encounter.locationId || encounter.room || '',
          department: encounter.department || encounter.unit || '',
          fin: encounter.fin || '',
          admissionDetails: encounter.admissionDetails || {}
        };

        // Extract patient information from various possible sources
        let patientData = null;
        if (encounter.patient) {
          // If patient object exists, use it
          patientData = {
            id: encounter.patient.id || encounter.patientId,
            name: encounter.patient.name || 'Unknown Patient',
            mrn: encounter.patient.mrn || encounter.patientMRN || 'N/A'
          };
        } else if (encounter.patientName) {
          // If patientName exists directly on encounter
          patientData = {
            id: encounter.patientId,
            name: encounter.patientName,
            mrn: encounter.patientMRN || 'N/A'
          };
        } else {
          // Fallback to basic patient info
          patientData = {
            id: encounter.patientId,
            name: 'Unknown Patient',
            mrn: 'N/A'
          };
        }

        return {
          ...encounterData,
          patient: patientData
        };
      });

      console.log('PatientDischarge: Processed encounters:', encountersWithPatients);
      console.log('PatientDischarge: First encounter sample:', encountersWithPatients[0]);
      setEncounters(encountersWithPatients);
    } catch (error) {
      console.error('Error fetching encounters:', error);
      setError('Failed to fetch encounters');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Refresh encounters
  const handleRefresh = async () => {
    await fetchEncounters();
  };

  // useEffect to fetch encounters on component mount
  useEffect(() => {
    if (user?.defaultFacility?.id) {
      fetchEncounters();
    }
  }, [user?.defaultFacility?.id, fetchEncounters]);

  // Handle discharge
  const handleDischarge = async () => {
    if (!selectedEncounter || !selectedPatient) {
      setError('Please select an encounter and patient');
      return;
    }

    try {
      setLoading(true);
      // Update encounter status to discharged
      await InpatientService.updateEncounterStatus(selectedEncounter.id, 'discharged', {
        dischargeDate: new Date().toISOString(),
        dischargeSummary: dischargeFormData.dischargeSummary || '',
        dischargeInstructions: dischargeFormData.dischargeInstructions || ''
      });

      // Update patient status
      await InpatientService.updatePatient(selectedPatient.id, {
        status: 'discharged',
        lastDischargeDate: new Date().toISOString()
      });

      // Discharge from bed/room if assigned
      if (selectedEncounter.bedUnit) {
        await dischargePatientFromBed(selectedEncounter.id);
      }
      if (selectedEncounter.locationId) {
        await dischargePatientFromRoom(selectedEncounter.id);
      }

      setSuccess('Patient discharged successfully');
      setSelectedEncounter(null);
      setSelectedPatient(null);
      setDischargeFormData({});
      
      // Refresh the encounters list
      await fetchEncounters();
    } catch (error) {
      console.error('Error discharging patient:', error);
      setError('Failed to discharge patient');
    } finally {
      setLoading(false);
    }
  };

  const handleEncounterSelect = (encounter) => {
    setSelectedEncounter(encounter);
    setSelectedPatient(encounter.patient);
    setDischargeFormData({
      disposition: 'Discharge',
      dischargeDate: new Date().toISOString().split('T')[0],
      comments: '',
      clinicalImpression: '',
      followUpInstructions: ''
    });
  };

  const filteredEncounters = encounters.filter(encounter => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const patientName = encounter.patient?.name || '';
    const mrn = encounter.patient?.mrn || '';
    const diagnosis = encounter.diagnosis || encounter.reason || '';
    
    return patientName.toLowerCase().includes(query) ||
           mrn.toLowerCase().includes(query) ||
           diagnosis.toLowerCase().includes(query);
  });

  // Debug logging
  console.log('PatientDischarge: encounters state:', encounters);
  console.log('PatientDischarge: filteredEncounters:', filteredEncounters);

  const getAdmissionDuration = (admissionDate) => {
    if (!admissionDate) return 'N/A';
    const admission = admissionDate.toDate ? admissionDate.toDate() : new Date(admissionDate);
    const now = new Date();
    const diffTime = Math.abs(now - admission);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  return (
    <Box sx={{ p: 2, background: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header */}
      <HeaderCard>
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Inpatient Discharge
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Manage patient discharges and discharge activities
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TooltipWrapper title="Refresh">
                <IconButton 
                  onClick={handleRefresh} 
                  sx={{ color: 'white' }}
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </TooltipWrapper>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      <Grid container spacing={2}>
        {/* Left Column - Patient List */}
        <Grid item xs={12} lg={4}>
          <CompactCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Active Inpatients ({filteredEncounters.length})
                </Typography>
              </Box>
              
              {/* Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2 }}
              />

              {/* Patient List */}
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredEncounters.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                    No active inpatients found
                  </Typography>
                ) : (
                  filteredEncounters.map((encounter) => (
                    <CompactCard
                      key={encounter.id}
                      sx={{
                        cursor: 'pointer',
                        border: selectedEncounter?.id === encounter.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                        '&:hover': {
                          borderColor: theme.palette.primary.light,
                          backgroundColor: theme.palette.action.hover
                        },
                        mb: 1,
                        boxShadow: 'none',
                        minHeight: 0
                      }}
                      onClick={() => handleEncounterSelect(encounter)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: theme.palette.primary.main, fontSize: 18 }}>
                            <PersonIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.1 }} noWrap>
                              {encounter.patient?.name || 'Unknown Patient'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1 }} noWrap>
                              MRN: {encounter.patient?.mrn || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip 
                            label={encounter.admissionDetails?.department || 'Unknown Unit'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {getAdmissionDuration(encounter.startDate)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.1 }} noWrap>
                          {encounter.diagnosis || encounter.reason || 'No diagnosis'}
                        </Typography>
                      </CardContent>
                    </CompactCard>
                  ))
                )}
              </Box>
            </CardContent>
          </CompactCard>
        </Grid>

        {/* Right Column - Discharge Details */}
        <Grid item xs={12} lg={8}>
          {selectedEncounter ? (
            <Box>
              {/* Patient Info Header */}
              <CompactCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 48, height: 48, mr: 2, bgcolor: theme.palette.primary.main }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {selectedPatient?.name || 'Unknown Patient'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          MRN: {selectedPatient?.mrn || 'N/A'} | 
                          Age: {selectedPatient?.dateOfBirth ? 
                            Math.floor((new Date() - new Date(selectedPatient.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)) : 'N/A'} | 
                          Room: {selectedEncounter.admissionDetails?.room || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ExitToAppIcon />}
                      onClick={handleDischarge}
                      disabled={loading}
                      sx={{ borderRadius: '8px' }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Discharge Patient'}
                    </Button>
                  </Box>
                </CardContent>
              </CompactCard>

              {/* Tabs */}
              <CompactCard>
                <CardContent sx={{ p: 0 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Tab label="Discharge Activity" />
                    <Tab label="Quick Discharge" />
                  </Tabs>
                </CardContent>
              </CompactCard>

              {/* Tab Content */}
              {activeTab === 0 && (
                <CompactCard>
                  <CardContent sx={{ p: 1 }}>
                    <Grid container spacing={1}>
                      {/* Left Column - Main Discharge Activities */}
                      <Grid item xs={12} lg={8}>
                        {/* Clinical Impression */}
                        <CompactCard>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <AssignmentIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.2rem' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                Clinical Impression
                              </Typography>
                              <Button 
                                variant="text" 
                                size="small" 
                                sx={{ ml: 'auto', color: theme.palette.primary.main, fontSize: '0.75rem' }}
                              >
                                Add from Problem List
                              </Button>
                            </Box>
                            <TextField
                              placeholder="Add a new impression"
                              value={dischargeFormData.clinicalImpression || ''}
                              onChange={(e) => setDischargeFormData({
                                ...dischargeFormData,
                                clinicalImpression: e.target.value
                              })}
                              multiline
                              rows={2}
                              fullWidth
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button variant="contained" startIcon={<AddIcon />} size="small" sx={{ fontSize: '0.75rem' }}>
                                Add
                              </Button>
                              <Button variant="outlined" startIcon={<PersonIcon />} size="small" sx={{ fontSize: '0.75rem' }}>
                                Associate
                              </Button>
                            </Box>
                          </CardContent>
                        </CompactCard>

                        {/* Bed Request */}
                        <CompactCard>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LocalHospitalIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.2rem' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                Bed Request
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<AddIcon />}
                                sx={{ ml: 'auto', fontSize: '0.75rem' }}
                              >
                                New Order
                              </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {['ED Admit Decision', 'ED Transfer to L&D', 'Place Patient in ED Psych'].map((option) => (
                                <Box key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FormControlLabel
                                    control={
                                      <Radio
                                        checked={dischargeFormData.bedRequestType === option}
                                        onChange={(e) => setDischargeFormData({
                                          ...dischargeFormData,
                                          bedRequestType: option
                                        })}
                                        size="small"
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontSize: '0.8rem' }}>
                                        {option}
                                      </Typography>
                                    }
                                  />
                                </Box>
                              ))}
                            </Box>
                          </CardContent>
                        </CompactCard>

                        {/* Orders & Prescriptions */}
                        <CompactCard>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <DescriptionIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.2rem' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                Orders & Prescriptions
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<AddIcon />}
                                sx={{ ml: 'auto', fontSize: '0.75rem' }}
                              >
                                New Order
                              </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              Orders and prescriptions will be displayed here
                            </Typography>
                          </CardContent>
                        </CompactCard>
                      </Grid>

                      {/* Right Column - Discharge Details */}
                      <Grid item xs={12} lg={4}>
                        {/* Disposition */}
                        <CompactCard>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LocalHospitalIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.2rem' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                Disposition
                              </Typography>
                            </Box>
                            
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <Select
                                value={dischargeFormData.disposition || 'Discharge'}
                                onChange={(e) => setDischargeFormData({
                                  ...dischargeFormData,
                                  disposition: e.target.value
                                })}
                                size="small"
                              >
                                {['Discharge', 'Transfer to Another Facility', 'HBR ED to UNC ED', 'AMA', 'Eloped', 'Expired', 'LWBS after Triage'].map((option) => (
                                  <MenuItem key={option} value={option} sx={{ fontSize: '0.8rem' }}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                              Comments
                            </Typography>
                            <TextField
                              placeholder="Add comments..."
                              multiline
                              rows={2}
                              fullWidth
                              size="small"
                              value={dischargeFormData.comments || ''}
                              onChange={(e) => setDischargeFormData({
                                ...dischargeFormData,
                                comments: e.target.value
                              })}
                              sx={{ mb: 1 }}
                            />
                          </CardContent>
                        </CompactCard>

                        {/* Follow-Up */}
                        <CompactCard>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <ScheduleIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.2rem' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                Follow-Up
                              </Typography>
                            </Box>
                            
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                              Suggestions
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                              {['PCP - None Specified', 'Other - Lookup', 'Other - Free Text'].map((suggestion, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AddIcon sx={{ color: 'success.main', fontSize: '0.8rem' }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{suggestion}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </CardContent>
                        </CompactCard>

                        {/* Best Practice Advisories */}
                        <CompactCard>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Chip 
                                label="BestPractice Advisories" 
                                size="small"
                                sx={{ 
                                  backgroundColor: theme.palette.warning.main, 
                                  color: 'white', 
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem'
                                }} 
                              />
                            </Box>
                            <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                Allergies have not been verified during this encounter.
                              </Typography>
                            </Alert>
                          </CardContent>
                        </CompactCard>
                      </Grid>
                    </Grid>
                  </CardContent>
                </CompactCard>
              )}

              {activeTab === 1 && (
                <CompactCard>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Disposition</InputLabel>
                          <Select
                            value={dischargeFormData.disposition || 'Discharge'}
                            onChange={(e) => setDischargeFormData({
                              ...dischargeFormData,
                              disposition: e.target.value
                            })}
                            label="Disposition"
                          >
                            <MenuItem value="Discharge">Discharge</MenuItem>
                            <MenuItem value="Transfer to Another Facility">Transfer to Another Facility</MenuItem>
                            <MenuItem value="AMA">AMA</MenuItem>
                            <MenuItem value="Expired">Expired</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="Discharge Date"
                          value={dischargeFormData.dischargeDate ? new Date(dischargeFormData.dischargeDate) : new Date()}
                          onChange={(date) => setDischargeFormData({
                            ...dischargeFormData,
                            dischargeDate: date ? date.toISOString().split('T')[0] : ''
                          })}
                          slotProps={{
                            textField: { fullWidth: true, size: 'small' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Comments"
                          multiline
                          rows={3}
                          value={dischargeFormData.comments || ''}
                          onChange={(e) => setDischargeFormData({
                            ...dischargeFormData,
                            comments: e.target.value
                          })}
                          placeholder="Add discharge comments..."
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </CompactCard>
              )}
            </Box>
          ) : (
            <CompactCard>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LocalHospitalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    Select a Patient
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a patient from the list to view discharge details
                  </Typography>
                </Box>
              </CardContent>
            </CompactCard>
          )}
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default PatientDischarge; 